<template>
  <div class="document">
    <h1 class="hidden-text">Dokumentvisning</h1>
    <sidebar :showSidebar="showSidebar" v-on:toggleSidebar="toggleSidebar($event)" :overlay="saving">
      <!-- SIDEBAR CONTENT IF EDITING -->
      <template v-if="editing && loggedIn">
        <h2 class="h5"><b-icon icon="hand-index-thumb" class="mr-3"></b-icon>Handlinger</h2>
        <b-button-group class="w-100">
          <b-button variant="primary" @click="cancelEditing()" :disabled="saving">Annullér</b-button>
          <b-button variant="secondary" @click="handleSave()" :disabled="saving">Gem</b-button>
        </b-button-group>
        <b-alert :show="showSaveAlert" variant="danger" class="mt-3">Du skal udfylde alle påkrævede felter før du kan gemme</b-alert>
        <b-alert :show="saveError !== ''" variant="danger" class="mt-3">Der opstod en fejl.<br><br>{{saveError}}<br><br>Prøv igen senere</b-alert>
        <hr>
        <h2 class="h5 mb-4"><b-icon icon="file-earmark-text" class="mr-3"></b-icon>Dokumentdetaljer</h2>
        <p class="h6 text-primary">Afgørelsesdato <span class="text-secondary">*</span></p>
        <b-form-datepicker
          id="ruling"
          v-model="docCopy.ruling_on"
          locale="da"
          placeholder="Vælg en dato"
          :state="docCopy.ruling_on !== null"
          today-variant="secondary"
          nav-button-variant="primary"
        >
          <template #button-content><b-icon icon="calendar"></b-icon><span class="hidden-text">Vælg en dato</span></template>
        </b-form-datepicker>
        <b-form-invalid-feedback>
          Du skal vælge en afgørelsesdato
        </b-form-invalid-feedback>
        <hr>
        <b-form-checkbox v-model="docCopy.published" name="published-switch" class="mb-3">
          Udgivet
        </b-form-checkbox>
        <p class="h6 text-primary">Udgivelsesdato <span v-if="docCopy.published" class="text-secondary">*</span></p>
        <b-form-group>
          <b-form-datepicker
            id="published"
            v-model="docCopy.published_on"
            locale="da"
            placeholder="Vælg en dato"
            :state="docCopy.published ? docCopy.published_on !== null : null" 
            :disabled="!docCopy.published"
            today-variant="secondary"
            nav-button-variant="primary"
          >
            <template #button-content><b-icon icon="calendar"></b-icon><span class="hidden-text">Vælg en dato</span></template>
          </b-form-datepicker>
          <b-form-invalid-feedback>
            Du skal vælge en udgivelsesdato
          </b-form-invalid-feedback>
        </b-form-group>
        <hr>
        <b-form-checkbox v-model="docCopy.nullified" name="applicable-switch" class="mb-3">
          Ophævet
        </b-form-checkbox>
        <p class="h6 text-primary">Ophævelsesdato <span v-if="docCopy.nullified" class="text-secondary">*</span></p>
        <b-form-group>
          <b-form-datepicker
            id="nullification"
            v-model="docCopy.nullified_on"
            locale="da"
            placeholder="Vælg en dato"
            :state="docCopy.nullified ? docCopy.nullified_on !== null : null"
            :disabled="!docCopy.nullified"
            today-variant="secondary"
            nav-button-variant="primary"
          >
            <template #button-content><b-icon icon="calendar"></b-icon><span class="hidden-text">Vælg en dato</span></template>
          </b-form-datepicker>
          <b-form-invalid-feedback>
            Du skal vælge en ophævelsesdato
          </b-form-invalid-feedback>
        </b-form-group>
        <hr>
        <b-form-group
          id="journal-number-fieldset"
          description=""
          label-for="journal-number"
          label-class="h6 text-primary"
        >
          <template #label>Journalnummer <span class="text-secondary">*</span></template>
          <b-form-input
            id="journal-number"
            v-model="docCopy.journal_number"
            trim
            :state="journalNumberValid"
            placeholder="XX-XXX-XXXX eller XX/XXXXX"
          ></b-form-input>
          <b-form-invalid-feedback>{{journalNumberError}}</b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          id="authority-fieldset"
          description=""
          label-for="authority"
          label-class="h6 text-primary"
        >
          <template #label>Myndighed <span class="text-secondary">*</span></template>
          <b-form-select id="authority" v-model="docCopy.authority.name" :options="authorityOptions" :state="docCopy.authority.name !== null" @change="setAuthority($event)"></b-form-select>
          <b-form-invalid-feedback>
            Du skal vælge en myndighed
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          id="doc-type-fieldset"
          description=""
          label-for="doc-type"
          label-class="h6 text-primary"
        >
          <template #label>Dokumenttype <span class="text-secondary">*</span></template>
          <b-form-select id="doc-type" v-model="docCopy.type" :options="typeOptions" :state="docCopy.type !== null"></b-form-select>
          <b-form-invalid-feedback>
            Du skal vælge en dokumenttype
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          id="classification-fieldset"
          description=""
          label-for="classification"
          label-class="h6 text-primary"
        >
          <template #label>Klassifikation <span class="text-secondary">*</span></template>
          <b-form-select id="classification" v-model="docCopy.classification" :options="classificationOptions" :state="docCopy.classification !== null"></b-form-select>
          <b-form-invalid-feedback>
            Du skal vælge en klassifikation
          </b-form-invalid-feedback>
        </b-form-group>
        <hr>
        <p class="h6 text-primary">Taksonomi</p>
        <keyword-select :keywords="docCopy.taxonomy" v-on:keywordsChange="setKeywords($event)"></keyword-select>
        <hr>
        <b-form-group
          id="labels-fieldset"
          description=""
          label="Øvrige emneord"
          label-for="labels"
          label-class="h6 text-primary"
          class="mb-0"
        >
          <b-form-tags input-id="labels" tag-variant="primary" v-model="docCopy.labels" placeholder="Tilføj emneord..." addButtonText="Tilføj"></b-form-tags>
        </b-form-group>
        <hr>
        <p class="mt-3"><small><em><span class="text-secondary">*</span> Påkrævet</em></small></p>
      </template>
      <!-- SIDEBAR CONTENT IF VIEWING -->
      <template v-else>
        <template v-if="doc && !error && !loading">
          <template v-if="loggedIn">
            <h2 class="h5"><b-icon icon="hand-index-thumb" class="mr-3"></b-icon>Handlinger</h2>
            <b-button-group class="w-100">
              <b-button @click="startEditing()">Redigér</b-button>
            </b-button-group>
            <hr>
          </template>
          <div class="filter-toggle d-flex pointer" v-b-toggle="'contents'">
            <h2 class="h5 mb-0 flex-fill"><b-icon icon="list-nested" class="mr-3"></b-icon>Indhold</h2>
            <b-button variant="link" class="p-0 h5 mb-0">
              <b-icon icon="chevron-down" class="toggle-icon"></b-icon>
              <span class="hidden-text">Vis/skjul indhold</span>
            </b-button>
          </div>
          <b-collapse id="contents" :visible="true" class="mt-2">
            <a href="#summary" class="h6 d-block">Resumé</a>
            <a href="#document" class="h6 d-block">Dokument</a>
            <a href="#sources" class="h6 d-block">Retskilder</a>
          </b-collapse>
          <hr>
          <h2 class="h5 mb-4"><b-icon icon="file-earmark-text" class="mr-3"></b-icon>Dokumentdetaljer</h2>
          <h3 class="h6">Afgørelsesdato</h3>
          <p class="mb-0">{{doc.ruling_on | moment('DD. MMMM YYYY')}}</p>
          <hr>
          <h3 class="h6">Udgivelsestilstand</h3>
          <p class="mb-3">
            <template v-if="doc.published">
              <b-icon icon="file-earmark-check" class="text-success"></b-icon> Udgivet
            </template>
            <template v-else>
              <b-icon icon="file-earmark-x" class="text-warning"></b-icon> Kladde
            </template>
          </p>
          <template v-if="doc.published">
            <h3 class="h6">Udgivelsesdato</h3>
            <p class="mb-0">{{doc.published_on | moment('DD. MMMM YYYY')}}</p>
            <hr>
          </template>
          <template v-if="doc.nullified">
            <b-badge class="mb-3">Ophævet</b-badge>
            <h3 class="h6">Ophævelsesdato</h3>
            <p class="mb-0">{{doc.nullified_on | moment('DD. MMMM YYYY')}}</p>
            <hr>
          </template>
          <h3 class="h6">Journalnummer</h3>
          <p class="mb-0" >{{doc.journal_number}}</p>
          <hr>
          <h3 class="h6">Myndighed</h3>
          <p class="mb-0">{{doc.authority.long_name}}</p>
          <hr>
          <h3 class="h6">Dokumenttype</h3>
          <p class="mb-0">{{doc.type}}</p>
          <hr>
          <h3 class="h6">Klassifikation</h3>
          <p class="mb-0">{{doc.classification}}</p>
          <hr>
          <h3 class="h6">Taksonomi</h3>
          <b-badge style="white-space:normal; text-align:left; line-height:1.5" variant="primary" v-if="doc.taxonomy.level_1">
            <template v-if="doc.taxonomy.level_1">{{doc.taxonomy.level_1.value}}&nbsp;</template>
            <template v-if="doc.taxonomy.level_2"><b-icon icon="chevron-right"></b-icon>&nbsp;{{doc.taxonomy.level_2.value}}&nbsp;</template>
            <template v-if="doc.taxonomy.level_3"><b-icon icon="chevron-right"></b-icon>&nbsp;{{doc.taxonomy.level_3.value}}&nbsp;</template>
            <template v-if="doc.taxonomy.level_4"><b-icon icon="chevron-right"></b-icon>&nbsp;{{doc.taxonomy.level_4.value}}</template>
          </b-badge>
          <span v-else><em>Ingen taksonomi</em></span>
          <hr>
          <h3 class="h6">Øvrige emneord</h3>
          <template v-if="cleanLabels.length">
            <b-badge
              v-for="(label, index) of cleanLabels"
              :key="doc.id + '-label-' + index"
              variant="primary"
              class="mr-2"
              @click.stop="searchLabel(label)"
            >{{label | labelize}}</b-badge>
          </template>
          <span v-else><em>Ingen emneord</em></span>
          <hr>
        </template>
      </template>
    </sidebar>
    <content-fit-sidebar :showSidebar="showSidebar" :overlay="saving">
      <!-- PAGE CONTENT IF LOADING -->
      <template v-if="loading">
        <div class="loading-area w-100 text-center">
          <b-spinner variant="primary" label="Henter dokument"></b-spinner>
        </div>
      </template>
      <!-- PAGE CONTENT WHEN LOADED -->
      <template v-else>
        <!-- PAGE CONTENT IF EDITING -->
        <template v-if="error">
          <b-alert variant="danger" show>{{error}}</b-alert>
        </template>
        <template v-else>
          <template v-if="editing">
            <div class=" title-field mb-3">
              <label class="h4 text-primary" for="title">Titel <span class="text-secondary">*</span></label>
              <b-input id="title" v-model="docCopy.title" :state="docCopy.title !== ''"></b-input>
              <b-form-invalid-feedback>
                Du skal indtaste en titel
              </b-form-invalid-feedback>
            </div>

            <div class="mb-3">
              <label class="h4 text-primary" for="summary">Resumé <span class="text-secondary">*</span></label>
              <editor
                id="summary"
                apiKey="bvtv0k3l32ywgtrypzpkaf1lcxq8lhg4uj4jjez261xz21jj"
                initialValue=""
                v-model="summary"
                :init="{
                  height: 250,
                  menubar: false,
                  theme: 'silver',
                  skin_url: '/oxide',
                  branding: false,
                  invalid_elements: 'div',
                  plugins: [
                    'advlist autolink lists link image charmap',
                    'searchreplace visualblocks code fullscreen',
                    'print preview anchor insertdatetime media',
                    'paste code help wordcount table',
                    'visualblocks'
                  ],
                  toolbar:
                    'undo redo visualblocks | formatselect | bold italic | \
                    alignleft aligncenter alignright alignjustify | \
                    bullist numlist outdent indent | table | help'
                }"
              ></editor>
            </div>

            <div class="mb-3">
              <label class="h4 text-primary" for="content">Dokument <span class="text-secondary">*</span></label>
              <editor
                id="content"
                apiKey="bvtv0k3l32ywgtrypzpkaf1lcxq8lhg4uj4jjez261xz21jj"
                initialValue=""
                v-model="content"
                :init="{
                  height: 600,
                  menubar: false,
                  theme: 'silver',
                  skin_url: '/oxide',
                  branding: false,
                  invalid_elements: 'div',
                  plugins: [
                    'advlist autolink lists link image charmap',
                    'searchreplace visualblocks code fullscreen',
                    'print preview anchor insertdatetime media',
                    'paste code help wordcount table',
                    'visualblocks'
                  ],
                  toolbar:
                    'undo redo visualblocks | formatselect | bold italic | \
                    alignleft aligncenter alignright alignjustify | \
                    bullist numlist outdent indent | table | help'
                }"
              ></editor>
            </div>
            
            <div class="mb-5 pb-4">
              <label class="h4 text-primary" for="sources">Retskilder <span class="text-secondary">*</span></label>
              <editor
                id="sources-editor"
                apiKey="bvtv0k3l32ywgtrypzpkaf1lcxq8lhg4uj4jjez261xz21jj"
                initialValue=""
                v-model="sources"
                :init="{
                  height: 250,
                  menubar: false,
                  theme: 'silver',
                  skin_url: '/oxide',
                  branding: false,
                  invalid_elements: 'div',
                  plugins: [
                    'advlist autolink lists link image charmap',
                    'searchreplace visualblocks code fullscreen',
                    'print preview anchor insertdatetime media',
                    'paste code help wordcount table',
                    'visualblocks'
                  ],
                  toolbar:
                    'undo redo visualblocks | formatselect | bold italic | \
                    alignleft aligncenter alignright alignjustify | \
                    bullist numlist outdent indent | table | help'
                }"
              ></editor>
            </div>
          </template>
          <!-- PAGE CONTENT IF VIEWING -->
          <template v-else>
            <div class="d-flex mb-3">
              <h3 id="document-title" class="flex-fill">{{doc.title}}</h3>
              <div v-if="doc.nullified">
                <b-badge variant="secondary">Ophævet</b-badge>
              </div>
            </div>
            <div class="print-details mb-4">
              <b-row>
                <b-col v-for="(item, index) of printDetails" :key="index" cols="4">
                  <div class="mb-4">
                    <h6>{{item.name}}</h6>
                    <hr class="mt-0 mb-1">
                    {{item.text}}
                  </div>
                </b-col>
              </b-row>
              <h3 class="h6">Taksonomi</h3>
              <hr class="mt-0 mb-1">
              <template v-if="doc.taxonomy.level_1">
                <template v-if="doc.taxonomy.level_1">{{doc.taxonomy.level_1.value}}&nbsp;</template>
                <template v-if="doc.taxonomy.level_2"><b-icon icon="chevron-right"></b-icon>&nbsp;{{doc.taxonomy.level_2.value}}&nbsp;</template>
                <template v-if="doc.taxonomy.level_3"><b-icon icon="chevron-right"></b-icon>&nbsp;{{doc.taxonomy.level_3.value}}&nbsp;</template>
                <template v-if="doc.taxonomy.level_4"><b-icon icon="chevron-right"></b-icon>&nbsp;{{doc.taxonomy.level_4.value}}</template>
              </template>
              <span v-else><em>Ingen taksonomi</em></span>
              <h6 class="mt-4">Øvrige emneord</h6>
              <hr class="mt-0 mb-1">
              <template v-if="cleanLabels.length">
                <span
                  v-for="(label, index) of cleanLabels"
                  :key="doc.id + '-label-' + index"
                  variant="primary"
                  class="mr-2"
                  @click.stop="searchLabel(label)"
                >{{label | labelize}},</span>
              </template>
              <span v-else><em>Ingen emneord</em></span>
            </div>
            <b-card class="mb-4 border-0 plain" bg-variant="light">
              <h4 class="position-relative">
                <span class="anchor" id="summary"></span>
                Resumé
              </h4>
              <div id="document-summary" class="mb-4" v-html="summary"></div>
            </b-card>
            <h4 class="position-relative">
              <span class="anchor" id="document"></span>
              Dokument
            </h4>
            <div id="document-content" v-html="content"></div>
            <b-card class="mb-0 border-0 plain" bg-variant="light">
              <h2 class="h4 position-relative text-primary" for="sources">
                <span class="anchor" id="sources"></span>
                Retskilder
              </h2>
              <div class="mb-4" id="document-sources" v-html="sources"></div>
            </b-card>
          </template>
        </template>
      </template>
    </content-fit-sidebar>
  </div>
</template>

<script>
// @ is an alias to /src
import Sidebar from '@/components/Sidebar.vue'
import ContentFitSidebar from '@/components/ContentFitSidebar.vue'
import Editor from '@tinymce/tinymce-vue'
import tinymce from 'tinymce/tinymce'
import 'tinymce/themes/silver/theme'
import 'tinymce/icons/default'
import 'tinymce/plugins/advlist'
import 'tinymce/plugins/autolink'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/link'
import 'tinymce/plugins/image'
import 'tinymce/plugins/charmap'
import 'tinymce/plugins/searchreplace'
import 'tinymce/plugins/visualblocks'
import 'tinymce/plugins/code'
import 'tinymce/plugins/fullscreen'
import 'tinymce/plugins/print'
import 'tinymce/plugins/preview'
import 'tinymce/plugins/anchor'
import 'tinymce/plugins/insertdatetime'
import 'tinymce/plugins/media'
import 'tinymce/plugins/paste'
import 'tinymce/plugins/code'
import 'tinymce/plugins/help'
import 'tinymce/plugins/wordcount'
import 'tinymce/plugins/table'
import 'tinymce/plugins/visualblocks'
import KeywordSelect from '../../components/KeywordSelect.vue'
export default {
  name: 'Dokument',
  components: {
    Sidebar,
    ContentFitSidebar,
    Editor,
    KeywordSelect
  },
  data: () => ({
    showSidebar: true,
    content: null,
    contentCopy: null,
    docCopy: null,
    summary: null,
    summaryCopy: null,
    sources: null,
    sourcesCopy: null,
    editing: false,
    saving: false,
    loading: true,
    error: null,
    doc: {
      title: '',
      journal_number: '',
      type: null,
      classification: null,
      authority: {
        name: null,
        long_name: null
      },
      ruling_on: null,
      labels: [],
      published: false,
      published_on: null,
      nullified: false,
      nullified_on: null,
      taxonomy: {
        keyword1: null,
        keyword2: null,
        keyword3: null,
        keyword4: null
      }
    },
    typeOptions: [
      { value: null, text: 'Vælg dokumenttype' },
      { value: 'Afgørelse', text: 'Afgørelse' },
      { value: 'Vejledende udtalelse', text: 'Vejledende udtalelse' }
    ],
    classificationOptions: [
      { value: null, text: 'Vælg klassifikation' },
      { value: 'Typisk', text: 'Typisk' },
      { value: 'Principiel', text: 'Principiel' }
    ],
    showSaveAlert: false,
    saveError: '',
    journalNumberError: ''
  }),
  mounted() {
    if (this.docId !== 'new') {
      this.getDocument()
    } else {
      this.content = ''
      this.summary = ''
      this.sources = ''
      this.loading = false
      this.startEditing()
    }
    tinymce.init({
      invalid_elements: 'div'
    })
  },
  computed: {
    printDetails() {
      let items = [
        { name: 'Afgørelsesdato', text: this.$options.filters.moment(this.doc.ruling_on, 'DD. MMMM YYYY')},
        { name: 'Udgivelsestilstand', text: this.doc.published ? 'Udgivet' : 'Kladde'},
      ]
      if (this.doc.published) {
        items.push({name: 'Udgivelsesdato', text: this.$options.filters.moment(this.doc.published_on, 'DD. MMMM YYYY')})
      }
      if (this.doc.nullified) {
        items.push({name: 'Ophævelsesdato', text: this.$options.filters.moment(this.doc.nullified_on, 'DD. MMMM YYYY')})
      }
      items.push(
        { name: 'Journalnr.', text: this.doc.journal_number},
        { name: 'Myndighed', text: this.doc.authority.long_name},
        { name: 'Dokumenttype', text: this.doc.type},
        { name: 'Klassifikation', text: this.doc.classification}
      )
      return items
    },
    taxonomyTopic() {
      if (this.docCopy && this.docCopy.taxonomy) {
        if (this.docCopy.taxonomy.level_4) return this.docCopy.taxonomy.level_4.id
        else if (this.docCopy.taxonomy.level_3) return this.docCopy.taxonomy.level_3.id
        else if (this.docCopy.taxonomy.level_2) return this.docCopy.taxonomy.level_2.id
        else if (this.docCopy.taxonomy.level_1) return this.docCopy.taxonomy.level_1.id
      }
      return null
    },
    loggedIn() {
      return sessionStorage.getItem('user')
    },
    docId() {
      return this.$route.params.id
    },
    authorities () {
      return this.$store.state.authorities
    },
    authorityOptions () {
      let options = [{value: null, text: 'Vælg myndighed'}]
      for (let item of this.authorities) {
        options.push({
          value: item.name,
          text: item.long_name
        })
      }
      return options
    },
    journalNumberValid() {
      let that = this
      if (this.docCopy.journal_number !== '' && this.docCopy.journal_number !== null) {
        that.journalNumberError = 'Journalnummeret er ikke korrekt formateret'
        return /([\d]{2}-[\d]{3}-[\d]{4})$|([\d]{2}\/[\d]{5})$/.test(this.docCopy.journal_number)
      } else if (this.docCopy.journal_number === '' || this.docCopy.journal_number === null) {
        that.journalNumberError = 'Journalnummeret skal udfyldes'
        return false
      }
      return true
    },
    cleanLabels() {
      return this.$store.getters.getCleanLabels(this.doc.labels)
    }
  },
  methods: {
    setAuthority(e) {
      this.docCopy.authority.long_name = this.getAuthorityLongName(e)
    },
    setKeywords(e) {
      this.docCopy.taxonomy = e
    },
    validate() {
      let value = true
      !this.journalNumberValid ? value = false : null
      this.docCopy.type === null ? value = false : null
      this.docCopy.authority === null ? value = false : null
      this.docCopy.classification === null ? value = false : null
      this.docCopy.title === '' ? value = false : null
      this.content === '' ? value = false : null
      this.summary === '' ? value = false : null
      this.sources === '' ? value = false : null
      this.docCopy.ruling_on === null ? value = false : null
      if (this.docCopy.published) {
        this.docCopy.published_on === null ? value = false : null
      }
      if (this.docCopy.nullified) {
        this.docCopy.nullified_on === null ? value = false : null
      }
      return value
    },
    getDocument() {
      let config = this.$store.getters.getConfig
      this.axios.get('/api/documents/' + this.docId, config).then((response) => {
        this.content = response.data.content
        this.summary = response.data.summary
        this.sources = response.data.sources
        this.doc = response.data
        this.loading = false
      }).catch(error => {
        console.error(error)
        this.error = error
        this.loading = false
      })
    },
    searchLabel(label) {
      this.$router.push({ name: 'Søgning', params: {label: label} })
    },
    toggleSidebar(value) {
      this.showSidebar = value
    },
    startEditing() {
      this.contentCopy = JSON.parse(JSON.stringify(this.content))
      this.summaryCopy = JSON.parse(JSON.stringify(this.summary))
      this.sourcesCopy = JSON.parse(JSON.stringify(this.sources))
      this.docCopy = JSON.parse(JSON.stringify(this.doc))
      this.editing = true
    },
    cancelEditing() {
      this.showSaveAlert = false
      this.saveError = ''
      if (this.docId === 'new') {
        this.$router.push('/')
      } else {
        this.content = JSON.parse(JSON.stringify(this.contentCopy))
        this.summary = JSON.parse(JSON.stringify(this.summaryCopy))
        this.sources = JSON.parse(JSON.stringify(this.sourcesCopy))
        this.editing = false
      }
    },
    handleSave() {
      if (this.docId === 'new') {
        this.createNewDocument()
      } else {
        this.saveDocument()
      }
    },
    getData() {
      return {
        content: this.content,
        summary: this.summary,
        sources: this.sources,
        title: this.docCopy.title,
        labels: this.docCopy.labels,
        classification: this.docCopy.classification,
        authority: this.docCopy.authority,
        type: this.docCopy.type,
        id: this.docCopy.id,
        ruling_on: this.docCopy.ruling_on,
        journal_number: this.docCopy.journal_number,
        published: this.docCopy.published,
        published_on: this.docCopy.published_on,
        nullified: this.docCopy.nullified,
        nullified_on: this.docCopy.nullified_on,
        taxonomy_topic: { id: this.taxonomyTopic }
      }
    },
    createNewDocument() {
      this.showSaveAlert = false
      if (this.validate()) {
        let config = this.$store.getters.getConfig
        this.saving = true
        let data = this.getData()
        this.axios.post('/api/documents/', data, config).then((response) => {
          this.$router.push(`/documents/${response.data.id}`)
          this.$router.go()
        }).catch(error => {
          this.saving = false
          this.saveError = error
          console.debug(error)
        })
      } else {
        this.showSaveAlert = true
      }
    },
    saveDocument() {
      this.showSaveAlert = false
      if (this.validate()) {
        let config = this.$store.getters.getConfig
        this.saving = true
        let data = this.getData()
        this.axios.put(`/api/documents/${this.docId}/`, data, config).then((response) => {
          this.doc = response.data
          console.debug(response)
          this.saving = false
          this.editing = false
        }).catch(error => {
          this.saving = false
          this.saveError = error
          console.debug(error)
        })
      } else {
        this.showSaveAlert = true
      }
    },
    getAuthorityLongName(name) {
      return this.$store.state.authorities.find(authority => authority.name === name).long_name
    }
  }
}
</script>
<style lang="scss">
.title-field {
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  
  &:before {
    content: attr(data-text);
    font-size: 1.5rem;
    color: rgba(0,0,0,.15);
    position: absolute;
    left: calc(0.75rem + 1px);
    top: 1px;
    line-height: 50px;
    pointer-events: none;
  }
  input {
    font-size: 1.5rem;
  }
}/* 

.placeholder {
  position: relative;
  display: inline-block;
  input {
    //width: fit-content;
  }  
}

.placeholder::after {
  position: absolute;
  right: 5px;
  top: 0;
  font-size: 1.5rem;
  line-height: 50px;
  content: attr(data-placeholder);
  pointer-events: none;
  opacity: 0.25;
} */

.anchor {
  position: absolute;
  top: -150px;
  left: 0;
}
</style>